import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

// SCRUM-216 (revised): Navigating guided tour.
// Each step auto-navigates to a page so the user sees it highlighted in the
// sidebar as they progress. Tour state is persisted to DB via POST /api/me/tour-seen.
// The sidebar's active-link highlighting works automatically via useLocation()
// — no extra props are needed on Sidebar.

function LogoMark({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <polygon
        points="16,3 27,9.6 27,22.4 16,29 5,22.4 5,9.6"
        stroke="#00C2A8" strokeWidth="1.5" fill="none" strokeLinejoin="round"
      />
      <line x1="16" y1="16" x2="16"   y2="8.6"  stroke="#00C2A8" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="16" y1="16" x2="22.4" y2="12.3" stroke="#00C2A8" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="16" y1="16" x2="9.6"  y2="12.3" stroke="#00C2A8" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="16" cy="16" r="2" fill="#00C2A8" />
    </svg>
  );
}

// Steps are path-aware. Pressing Next navigates to the next step's path so the
// sidebar highlights the correct nav item automatically.
//
// Role-based step sets:
//   admin / owner  → Dashboard → Portfolio Health → Reports → Backlog AI → AI Context → Done
//   viewer         → Dashboard → Reports → Done
function buildSteps(userRole, userPlan) {
  const canEdit = ['admin', 'owner'].includes(userRole);
  const isFree = userPlan === 'free' || (!userPlan);

  const steps = [
    {
      title: 'Welcome to your dashboard',
      body: 'This is your live view of all connected Jira projects. Health scores, sprint progress, and AI insights update automatically — no manual work needed.',
      path: '/',
    },
  ];

  // Portfolio Health is visible for admins/owners and free-plan users (sandbox)
  if (canEdit || isFree) {
    steps.push({
      title: 'Portfolio health',
      body: "Get a bird's eye view across all your projects. Velocity trends, blocker counts, and composite health scores give you a quick read on what needs attention.",
      path: '/portfolio',
    });
  }

  steps.push({
    title: 'Automated reports',
    body: 'Your full history of weekly executive summaries — each exportable as PDF or CSV and shareable via a private link.',
    path: '/reports',
  });

  if (canEdit) {
    steps.push({
      title: 'Backlog AI — AI-generated insights',
      body: 'AI-powered backlog analysis grounded in your real Jira data. Spot gaps, get refinement suggestions, and see ticket quality scores without leaving the app.',
      path: '/backlog',
    });
    steps.push({
      title: 'Personalise your AI context',
      body: "Set your product vision, strategy, and goals in Profile → AI Context. This shapes every AI insight and report to your organisation's priorities.",
      path: '/profile',
    });
  } else if (isFree) {
    steps.push({
      title: 'Backlog AI — Premium',
      body: 'AI-powered backlog analysis that spots gaps, suggests refinements, and scores ticket quality — all grounded in your real Jira data. Available with Premium.',
      path: null,
    });
  }

  steps.push({
    title: "You're all set",
    body: "That's the full tour. Everything updates live from Jira — check back after your next sprint for fresh insights and your automated weekly report.",
    path: null,
  });

  return steps;
}

export default function GuidedTour({ user, onDismiss }) {
  const [step, setStep] = useState(0);
  const [navigating, setNavigating] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const navigate = useNavigate();

  const steps = buildSteps(user?.role, user?.plan);
  const total = steps.length;
  const current = steps[step];
  const isLast = step === total - 1;

  const dismiss = async () => {
    if (dismissing) return;
    setDismissing(true);
    try {
      await axios.post('/api/me/tour-seen');
    } catch {
      // Non-blocking — if the request fails, the tour will reappear on next load.
    }
    onDismiss();
  };

  // Navigate to a step index. If the target step has a path, navigate there first
  // and wait briefly so the page content starts to render before the tooltip updates.
  const goToStep = async (nextStep) => {
    const target = steps[nextStep];
    if (target?.path) {
      setNavigating(true);
      navigate(target.path);
      // Allow time for the page to start loading (React Router navigation is instant
      // but lazy-loaded pages need a moment to mount their skeleton).
      await new Promise(r => setTimeout(r, 600));
      setNavigating(false);
    }
    setStep(nextStep);
  };

  const next = () => {
    if (isLast) {
      dismiss();
    } else {
      goToStep(step + 1);
    }
  };

  // Repeat: restart the tour from the dashboard without marking it as seen.
  const repeat = () => {
    goToStep(0);
  };

  const busy = dismissing || navigating;

  return (
    // Fixed banner at the bottom of the viewport — outer div is pointer-events-none
    // so the tour strip does not block clicks on page content beneath it.
    <div
      role="region"
      className="fixed bottom-0 left-0 right-0 z-50 px-4 pb-4 pointer-events-none"
      aria-live="polite"
      aria-label="Guided tour"
    >
      <div className="max-w-2xl mx-auto bg-[#1A2E45] border border-[#00C2A8]/30 rounded-xl p-4 shadow-2xl pointer-events-auto">

        {/* Header row */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <LogoMark size={18} />
            <span className="text-xs font-bold uppercase tracking-widest text-[#00C2A8]">
              Tour · {step + 1}/{total}
            </span>
          </div>

          <div className="flex items-center gap-2">
            {/* Repeat button only on final step */}
            {isLast && (
              <button
                onClick={repeat}
                disabled={busy}
                className="text-xs text-[#00C2A8] hover:text-[#009987] px-2 py-1.5 transition-colors disabled:opacity-50"
              >
                ↩ Repeat
              </button>
            )}
            <button
              onClick={dismiss}
              disabled={busy}
              className="text-xs text-[#E5C07B] hover:text-white px-2 py-1.5 transition-colors disabled:opacity-50"
            >
              Skip tour
            </button>
            <button
              onClick={next}
              disabled={busy}
              className="bg-[#00C2A8] text-[#0D1B2A] text-sm font-semibold px-4 py-1.5 rounded-lg hover:bg-[#009987] transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              {/* Inline spinner while navigating */}
              {navigating && (
                <span
                  className="w-3 h-3 border-2 border-[#0D1B2A]/30 border-t-[#0D1B2A] rounded-full animate-spin"
                  aria-hidden="true"
                />
              )}
              {isLast ? 'Done ✓' : 'Next →'}
            </button>
          </div>
        </div>

        {/* Step content */}
        <p className="font-semibold text-white text-sm mb-1" style={{ fontFamily: 'Syne, sans-serif' }}>
          {current.title}
        </p>
        <p className="text-sm text-[#E5C07B] leading-relaxed">{current.body}</p>

        {/* Progress bar */}
        <div className="flex gap-1 mt-3">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`h-0.5 flex-1 rounded-full transition-all duration-300 ${
                i <= step ? 'bg-[#00C2A8]' : 'bg-[#243B55]'
              }`}
            />
          ))}
        </div>

      </div>
    </div>
  );
}
